import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/Button';
import { Menu, X, ChevronDown, Instagram, Linkedin, Facebook } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PolygotsLogo } from './PolygotsLogo';

export const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isCareersOpen, setIsCareersOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsCareersOpen(false);
    }, [location]);

    const navLinks = [
        { name: 'For Talents', href: '/login?role=candidate' },
        { name: 'For Employers', href: '/login?role=recruiting_partner' },
        { name: 'For Partner', href: '/login?role=sourcing_partner' },
        { name: 'About Us', href: '/about' },
    ];

    const careerLinks = [
        { name: 'Healthcare', href: '/careers/healthcare' },
        { name: 'Technical & Industrial', href: '/careers/technical-industrial' },
        { name: 'Logistics & Transport', href: '/careers/logistics-transport' },
        { name: 'Vocational Programs', href: '/careers/vocational-programs' },
    ];

    const socialLinks = [
        { icon: Instagram, href: 'https://www.instagram.com/newpolyglots_academy?igsh=MTJxcDBpeWFjYXdvMw==', label: 'Instagram' },
        { icon: Linkedin, href: 'https://www.linkedin.com/company/polyglots-talent-connect/', label: 'LinkedIn' },
        { icon: Facebook, href: 'https://www.facebook.com/share/17VJ4QX4E6/', label: 'Facebook' },
    ];

    return (
        <nav
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-space-blue/80 backdrop-blur-lg border-b border-slate-800 py-4' : 'bg-transparent py-6'}`}
        >
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between">
                    <Link to="/" className="flex items-center group -ml-12 lg:-ml-16">
                        <PolygotsLogo size="default" className="group-hover:scale-105 transition-transform duration-300" />
                    </Link>

                    {/* Desktop Nav */}
                    <div className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <Link
                                key={link.name}
                                to={link.href}
                                className="text-slate-300 hover:text-white font-medium transition-colors relative group"
                            >
                                {link.name}
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-cyan-400 transition-all duration-300 group-hover:w-full" />
                            </Link>
                        ))}

                        {/* Careers Dropdown */}
                        <div
                            className="relative group"
                            onMouseEnter={() => setIsCareersOpen(true)}
                            onMouseLeave={() => setIsCareersOpen(false)}
                        >
                            <button className="text-slate-300 hover:text-white font-medium transition-colors flex items-center gap-1">
                                Careers
                                <ChevronDown className="w-4 h-4" />
                            </button>
                            <AnimatePresence>
                                {isCareersOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        className="absolute top-full left-0 mt-2 w-56 bg-slate-900 border border-slate-800 rounded-lg shadow-xl overflow-hidden"
                                    >
                                        {careerLinks.map((link) => (
                                            <Link
                                                key={link.name}
                                                to={link.href}
                                                className="block px-4 py-3 text-slate-300 hover:text-white hover:bg-cyan-500/10 transition-colors"
                                            >
                                                {link.name}
                                            </Link>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    <div className="hidden md:flex items-center gap-4">
                        {/* Social Media Icons */}
                        <div className="flex items-center gap-2 mr-2">
                            {socialLinks.map((social) => (
                                <a
                                    key={social.label}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-cyan-500 hover:text-white transition-all duration-300"
                                    aria-label={social.label}
                                >
                                    <social.icon className="w-4 h-4" />
                                </a>
                            ))}
                        </div>
                        <Link to="/login">
                            <Button variant="ghost" className="text-white hover:bg-white/10">
                                Log In
                            </Button>
                        </Link>
                        <Link to="/signup">
                            <Button variant="primary">Get Started</Button>
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden text-white p-2"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    >
                        {isMobileMenuOpen ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="md:hidden bg-space-blue border-b border-slate-800 overflow-hidden"
                    >
                        <div className="container mx-auto px-4 py-6 space-y-4">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.href}
                                    className="block text-slate-300 hover:text-white font-medium py-2"
                                >
                                    {link.name}
                                </Link>
                            ))}

                            {/* Careers Mobile */}
                            <div>
                                <div className="text-slate-300 font-medium py-2">Careers</div>
                                <div className="pl-4 space-y-2">
                                    {careerLinks.map((link) => (
                                        <Link
                                            key={link.name}
                                            to={link.href}
                                            className="block text-slate-400 hover:text-white py-1"
                                        >
                                            {link.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>

                            {/* Social Media Mobile */}
                            <div className="flex gap-3 pt-2">
                                {socialLinks.map((social) => (
                                    <a
                                        key={social.label}
                                        href={social.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-cyan-500 hover:text-white transition-all"
                                        aria-label={social.label}
                                    >
                                        <social.icon className="w-5 h-5" />
                                    </a>
                                ))}
                            </div>

                            <div className="pt-4 flex flex-col gap-3">
                                <Link to="/login" className="w-full">
                                    <Button
                                        variant="ghost"
                                        className="w-full text-white hover:bg-white/10"
                                    >
                                        Log In
                                    </Button>
                                </Link>
                                <Link to="/signup" className="w-full">
                                    <Button variant="primary" className="w-full">
                                        Get Started
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};
